import { availabilitySlotFor } from './format';
import type { CompatibilityResult, PlayerProfile, SessionSummary } from './types';

// Explainable, weighted compatibility — never a black box. Mirrors the tiers
// in architecture/matchmaking.md: strong fit (time, platform, language,
// voice), medium fit (play style), plus open-spot practicality.
export function scoreSessionCompatibility(user: PlayerProfile, session: SessionSummary): CompatibilityResult {
  const reasons: string[] = [];
  const warnings: string[] = [];
  let score = 35;

  if (user.games.includes(session.game)) {
    score += 16;
    reasons.push('You play this game');
  } else {
    warnings.push('Game is not on your profile yet');
  }

  if (session.platform === 'Crossplay' || user.platforms.includes(session.platform)) {
    score += 14;
    reasons.push('Platform works for you');
  } else {
    score -= 15;
    warnings.push('Platform may not match your profile');
  }

  if (user.languages.includes(session.language)) {
    score += 10;
    reasons.push('Language matches');
  }

  if (session.voice === 'optional' || user.voice === 'optional' || user.voice === session.voice) {
    score += 10;
    reasons.push('Voice preference is compatible');
  } else if (session.voice === 'required' && user.voice === 'none') {
    score -= 18;
    warnings.push('Host requires voice chat');
  }

  if (user.playStyle === session.style) {
    score += 14;
    reasons.push('Play style matches');
  } else if (user.playStyle === 'balanced' || session.style === 'balanced') {
    score += 8;
    reasons.push('Balanced play style should fit');
  } else if (session.style === 'teaching' && user.playStyle === 'casual') {
    score += 7;
    reasons.push('Teaching group is friendly to casual players');
  } else {
    warnings.push('Play style may need a vibe check');
  }

  const start = new Date(session.startTime);
  if (!Number.isNaN(start.getTime()) && user.availability.includes(availabilitySlotFor(start))) {
    score += 12;
    reasons.push('Matches your availability');
  }

  if (session.openSpots > 0) {
    score += 5;
    reasons.push(`${session.openSpots} open ${session.openSpots === 1 ? 'spot' : 'spots'}`);
  }

  score = Math.max(0, Math.min(100, score));

  return {
    score,
    level: score >= 92 ? 'Perfect fit' : score >= 82 ? 'Great fit' : score >= 70 ? 'Good fit' : score >= 55 ? 'Okay fit' : 'Low fit',
    reasons: reasons.slice(0, 4),
    warnings
  };
}
